import { useCallback, useRef, useState } from "react";

import { Desktop, Mobile } from "../../responsive/Responsive";
import DesktopModal from "../desktop-modal/DesktopModal";
import SlidingModal from "../sliding-modal/SlidingModal";
import { GiftCardPurchase } from "../../gift-card-purchase/GiftCardPurchase";
import useWindowSize from "../../../hooks/useWindowSize";

import "./GiftCardModal.scss";

// Buying a gift card, over the salon page rather than on a page of its own -
// the buyer stays where they were and there is no back-navigation to get wrong.
//
// Centred dialog on desktop, sheet sliding up from the bottom on mobile, which
// is the same split the rest of the app uses (see FormattedTextModal). The
// form itself is shared, so the two can never drift apart, and the standalone
// /company/:slug/gift-card route renders the same component for shared links.

// SlidingModal's header is a fixed 64px (see SlidingModal.scss).
const SHEET_HEADER_HEIGHT = 64;
// The highest the sheet's top edge goes, so a little of the page always shows
// above it. A form taller than that scrolls inside the sheet instead.
const SHEET_MIN_TOP = 64;

interface IGiftCardModalProps {
  show: boolean;
  onHide: () => void;
  companyId?: string;
  companyName?: string | null;
  userPaysFees?: boolean | null;
  recoveryOnly?: boolean;
}

export const GiftCardModal = ({
  show,
  onHide,
  companyId,
  companyName,
  userPaysFees,
  recoveryOnly
}: IGiftCardModalProps) => {
  const { height: windowHeight } = useWindowSize();
  const [formHeight, setFormHeight] = useState(0);
  const formObserver = useRef<ResizeObserver | null>(null);
  const pendingFrame = useRef(0);

  // On mobile the sheet hugs the form, so the button ends the sheet rather
  // than floating above a band of empty space. slideTo is the sheet's top edge,
  // not a height, so it is worked out from the form's height - and follows it
  // as that changes: between steps, "Someone else" vs "Myself", an error.
  //
  // Not SlidingModal's align="bottom": that only looks for its content when it
  // first mounts, before this form exists (it renders only while open), and it
  // never caps the sheet at the height of the screen.
  const measureForm = useCallback((node: HTMLDivElement | null) => {
    formObserver.current?.disconnect();
    formObserver.current = null;
    cancelAnimationFrame(pendingFrame.current);
    if (!node) return;
    formObserver.current = new ResizeObserver(() => {
      const height = Math.ceil(node.getBoundingClientRect().height);
      // 0 while the sheet is still display:none on its way in.
      if (!height) return;
      // Stored on the next frame, not inside this callback. React 17 renders
      // synchronously here, and framer-motion writes the sheet's styles during
      // that render, so the form can resize mid-callback - which the browser
      // reports as "ResizeObserver loop completed with undelivered
      // notifications".
      cancelAnimationFrame(pendingFrame.current);
      pendingFrame.current = requestAnimationFrame(() => setFormHeight(height));
    });
    formObserver.current.observe(node);
  }, []);

  const sheetTop = Math.max(
    SHEET_MIN_TOP,
    windowHeight - SHEET_HEADER_HEIGHT - formHeight
  );

  const renderForm = (ref?: (node: HTMLDivElement | null) => void) => (
    <div className="gift-card-modal__content" ref={ref}>
      <GiftCardPurchase
        companyId={companyId}
        companyName={companyName}
        userPaysFees={userPaysFees}
        recoveryOnly={recoveryOnly}
        onDone={onHide}
      />
    </div>
  );

  return (
    <>
      <Desktop>
        <DesktopModal
          className="gift-card-modal"
          title="Buy a gift card"
          show={show}
          onHide={onHide}
        >
          {show && renderForm()}
        </DesktopModal>
      </Desktop>
      <Mobile>
        <SlidingModal
          className="gift-card-modal"
          slideTo={sheetTop}
          enableScroll
          title="Buy a gift card"
          show={show}
          onHide={onHide}
        >
          {show && renderForm(measureForm)}
        </SlidingModal>
      </Mobile>
    </>
  );
};

export default GiftCardModal;
